/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: WebSocket Implementation
 * PURPOSE: Real-time permission synchronization with cache-first strategy
 *
 * NEW PATTERNS IMPLEMENTED:
 * - Service Pattern: Singleton service with lifecycle management
 * - Cache-First Strategy: Load from localStorage cache, validate in background
 * - Event-Driven Updates: WebSocket messages trigger permission refresh
 *
 * KEY FEATURES:
 * - 24-hour permission cache with version management
 * - Background validation after 2-second delay
 * - WebSocket listener for real-time permission updates
 * - Memory leak prevention with cleanup on page unload
 *
 * RELATED FILES:
 * - src/lib/state/auth.state.svelte.ts
 * - src/lib/services/api/profile.api.ts
 */

// src/lib/services/websocket/permission-sync.ts
import { browser } from '$app/environment';
import { authState, setFreshPermissions } from '$lib/state/auth.state.svelte';
import { websocketService } from './WebSocketService';
import { eventBus } from '$lib/core/services/EventBusService';
import { profileApi } from '$lib/services/api/profile.api';
import { createLogger } from '$lib/utils/logger';
import type { UserPermissions } from '$lib/types/core/user.types';
import type { ServerUpdateMessage } from '$lib/types/websocket/messages.types';

const logger = createLogger('websocket.permission-sync');

const PERMISSION_CACHE_KEY = 'user_permissions';
const PERMISSION_CACHE_VERSION = '1.0';
const PERMISSION_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

export interface PermissionUpdateMessage {
	type: 'permissions_updated';
	reason:
		| 'role_change'
		| 'permission_change'
		| 'component_change'
		| 'department_change'
		| 'organization_change';
	user_ids?: string[];
	component_id?: string;
	broadcast_to?: 'all';
	details?: any;
	timestamp: string;
}

export class PermissionSyncService {
	private static instance: PermissionSyncService;
	private initialized = false;
	private currentUserId: string | null = null;
	private eventListenerBound: ((data: unknown) => void | Promise<void>) | null = null;
	private beforeUnloadHandler: (() => void) | null = null;

	private constructor() {}

	static getInstance(): PermissionSyncService {
		if (!PermissionSyncService.instance) {
			PermissionSyncService.instance = new PermissionSyncService();
		}
		return PermissionSyncService.instance;
	}

	/**
	 * Initialize the permission sync service
	 */
	async initialize(userId: string): Promise<void> {
		if (this.initialized && this.currentUserId === userId) {
			logger.debug('Permission sync service already initialized', { userId });
			return;
		}

		this.currentUserId = userId;

		// Load permissions from cache first (instant UI)
		this.loadFromCache();

		// Register WebSocket listener for permission updates
		this.registerWebSocketListener();

		// Validate permissions in background
		this.validatePermissionsInBackground();

		// Register cleanup on page unload to prevent memory leaks
		this.beforeUnloadHandler = () => this.cleanup();
		if (browser) {
			window.addEventListener('beforeunload', this.beforeUnloadHandler);
		}

		this.initialized = true;
		logger.info('Permission sync service initialized successfully', {
			userId,
			features: ['cache', 'websocket', 'background_validation', 'cleanup_handler']
		});
	}

	/**
	 * Load permissions from localStorage cache
	 */
	private loadFromCache(): void {
		if (!browser) return;

		try {
			const cacheKey = `${PERMISSION_CACHE_KEY}_${this.currentUserId}`;
			const cached = localStorage.getItem(cacheKey);

			if (cached) {
				const { data, version, timestamp } = JSON.parse(cached);

				// Check cache version and TTL
				const now = Date.now();
				const age = now - timestamp;

				if (version === PERMISSION_CACHE_VERSION && age < PERMISSION_CACHE_TTL) {
					// Update authState with cached permissions
					setFreshPermissions({ direct_permissions: data.direct_permissions || [] });
					logger.debug('Loaded permissions from cache', {
						userId: this.currentUserId,
						cacheAgeMinutes: Math.round(age / 1000 / 60),
						permissionCount: data.direct_permissions?.length || 0,
						version
					});
				} else {
					// Cache expired or version mismatch
					localStorage.removeItem(cacheKey);
					logger.debug('Permission cache expired or outdated', {
						userId: this.currentUserId,
						version,
						expectedVersion: PERMISSION_CACHE_VERSION,
						ageMinutes: Math.round(age / 1000 / 60),
						ttlMinutes: PERMISSION_CACHE_TTL / 1000 / 60
					});
				}
			}
		} catch (error) {
			logger.error('Failed to load permissions from cache', {
				userId: this.currentUserId,
				error: error instanceof Error ? error.message : 'Unknown error'
			});
		}
	}

	/**
	 * Save permissions to localStorage cache
	 */
	private saveToCache(permissions: UserPermissions): void {
		if (!browser || !this.currentUserId) return;

		try {
			const cacheKey = `${PERMISSION_CACHE_KEY}_${this.currentUserId}`;
			const cacheData = {
				data: permissions,
				version: PERMISSION_CACHE_VERSION,
				timestamp: Date.now()
			};

			localStorage.setItem(cacheKey, JSON.stringify(cacheData));
			logger.debug('Saved permissions to cache', {
				userId: this.currentUserId,
				permissionCount: permissions.direct_permissions?.length || 0,
				version: PERMISSION_CACHE_VERSION
			});
		} catch (error) {
			logger.error('Failed to save permissions to cache', {
				userId: this.currentUserId,
				error: error instanceof Error ? error.message : 'Unknown error'
			});
		}
	}

	/**
	 * Register WebSocket listener for permission updates
	 */
	private registerWebSocketListener(): void {
		// Store the bound function so we can remove it later
		this.eventListenerBound = this.handlePermissionUpdate.bind(this);
		// Listen for WebSocket messages on the 'updates' topic
		eventBus.on('websocket:message:updates', this.eventListenerBound);
		logger.debug('WebSocket listener registered', {
			userId: this.currentUserId,
			topic: 'websocket:message:updates'
		});
	}

	/**
	 * Handle permission update from WebSocket
	 */
	private async handlePermissionUpdate(data: unknown): Promise<void> {
		const message = data as ServerUpdateMessage;
		// Check if this is a permission update message
		// The message.data should have update_type field for permission updates
		if (!message.data || (message.data as any).update_type !== 'permissions_updated') {
			return;
		}

		const updateData = message.data as unknown as PermissionUpdateMessage;

		// Check if this update affects the current user
		const affectsCurrentUser =
			updateData.broadcast_to === 'all' ||
			(updateData.user_ids &&
				this.currentUserId &&
				updateData.user_ids.includes(this.currentUserId));

		if (!affectsCurrentUser) {
			logger.debug('Permission update ignored (not for current user)', {
				userId: this.currentUserId,
				reason: updateData.reason,
				broadcastTo: updateData.broadcast_to,
				targetUserCount: updateData.user_ids?.length
			});
			return;
		}

		logger.debug('Permission update received', {
			userId: this.currentUserId,
			reason: updateData.reason,
			broadcastTo: updateData.broadcast_to,
			componentId: updateData.component_id,
			timestamp: updateData.timestamp
		});

		// Fetch new permissions from backend
		await this.refreshPermissions();

		// Emit event for UI components to react
		eventBus.emit('permissions:updated', {
			reason: updateData.reason,
			details: updateData.details
		});
	}

	/**
	 * Refresh permissions from backend
	 */
	async refreshPermissions(): Promise<void> {
		const startTime = Date.now();
		try {
			logger.debug('Fetching updated permissions from backend', {
				userId: this.currentUserId
			});

			const profile = await profileApi.getCurrentProfile();
			const permissions = this.extractPermissionsFromProfile(profile);

			// Update authState
			setFreshPermissions({ direct_permissions: permissions.direct_permissions || [] });

			// Update cache
			this.saveToCache(permissions);

			logger.info('Permissions refreshed successfully', {
				userId: this.currentUserId,
				permissionCount: permissions.direct_permissions?.length || 0,
				duration: Date.now() - startTime
			});
		} catch (error) {
			logger.error('Failed to refresh permissions from backend', {
				userId: this.currentUserId,
				error: error instanceof Error ? error.message : 'Unknown error',
				duration: Date.now() - startTime
			});
			// Keep using cached/current permissions on error
		}
	}

	/**
	 * Validate cached permissions against backend in background
	 */
	private async validatePermissionsInBackground(): Promise<void> {
		// Don't validate immediately, give time for cache to be used
		setTimeout(async () => {
			const startTime = Date.now();
			try {
				logger.debug('Starting background permission validation', {
					userId: this.currentUserId,
					delayMs: 2000
				});

				const profile = await profileApi.getCurrentProfile();
				const freshPermissions = this.extractPermissionsFromProfile(profile);

				// Get current permissions from authState
				const currentPermissions = authState.permissions;

				// Compare and update if different
				if (JSON.stringify(currentPermissions) !== JSON.stringify(freshPermissions)) {
					logger.info('Permissions changed during validation', {
						userId: this.currentUserId,
						oldCount: currentPermissions?.direct_permissions?.length || 0,
						newCount: freshPermissions.direct_permissions?.length || 0,
						duration: Date.now() - startTime
					});

					setFreshPermissions({ direct_permissions: freshPermissions.direct_permissions || [] });
					this.saveToCache(freshPermissions);

					// Emit event
					eventBus.emit('permissions:validated', {
						hadChanges: true
					});
				} else {
					logger.debug('Cached permissions validated successfully', {
						userId: this.currentUserId,
						permissionCount: freshPermissions.direct_permissions?.length || 0,
						duration: Date.now() - startTime
					});
				}
			} catch (error) {
				logger.error('Failed to validate permissions in background', {
					userId: this.currentUserId,
					error: error instanceof Error ? error.message : 'Unknown error',
					duration: Date.now() - startTime
				});
			}
		}, 2000); // 2 second delay
	}

	/**
	 * Clear permission cache for current user
	 */
	clearCache(): void {
		if (!browser || !this.currentUserId) return;

		const cacheKey = `${PERMISSION_CACHE_KEY}_${this.currentUserId}`;
		localStorage.removeItem(cacheKey);
		logger.debug('Permission cache cleared', {
			userId: this.currentUserId
		});
	}

	/**
	 * Extract permissions from user profile
	 */
	private extractPermissionsFromProfile(profile: any): UserPermissions {
		logger.debug('Extracting permissions from profile', {
			userId: this.currentUserId,
			hasProfile: !!profile,
			hasRoles: !!profile?.roles,
			roleCount: profile?.roles?.length
		});

		const permissions: UserPermissions = {
			dashboards: {},
			features: {},
			direct_permissions: [],
			inherited_permissions: [],
			metadata: {}
		};

		if (profile.roles && profile.roles.length > 0) {
			profile.roles.forEach((role: any) => {
				logger.debug('Processing role permissions', {
					userId: this.currentUserId,
					roleName: role.name,
					roleCode: role.code,
					hasPermissions: !!role.permissions,
					resourceCount: Object.keys(role.permissions || {}).length
				});

				if (role.permissions) {
					Object.entries(role.permissions).forEach(([resource, actions]) => {
						// Add to direct_permissions in the correct format
						permissions.direct_permissions.push({
							resource,
							actions: Array.isArray(actions) ? actions : [actions],
							scope: role.code
						});
					});
				}
			});
		}

		logger.debug('Permissions extracted from profile', {
			userId: this.currentUserId,
			directPermissionCount: permissions.direct_permissions.length,
			hasComponentPermissions: permissions.direct_permissions.some(
				(p) => p.resource === 'COMPONENT'
			),
			resources: [...new Set(permissions.direct_permissions.map((p) => p.resource))]
		});

		return permissions;
	}

	/**
	 * Cleanup when user logs out
	 */
	cleanup(): void {
		const userId = this.currentUserId;

		this.clearCache();
		this.currentUserId = null;
		this.initialized = false;

		// Remove event listener using the stored bound function
		if (this.eventListenerBound) {
			eventBus.off('websocket:message:updates', this.eventListenerBound);
			this.eventListenerBound = null;
		}

		// Remove page unload listener to prevent memory leaks
		if (this.beforeUnloadHandler && browser) {
			window.removeEventListener('beforeunload', this.beforeUnloadHandler);
			this.beforeUnloadHandler = null;
		}

		logger.info('Permission sync service cleaned up', {
			userId,
			cleanedResources: ['cache', 'websocket_listener', 'unload_handler']
		});
	}
}

// Export singleton instance
export const permissionSyncService = PermissionSyncService.getInstance();
